import { useState, type FormEvent } from 'react';
import { api, ErreurApi } from '../api/client';
import { useAuth } from './AuthContext';

/**
 * Changement de mot de passe.
 *
 * L'API révoque toutes les sessions au changement : on repasse donc par
 * l'écran de connexion, ce qui est aussi la vérification que le nouveau mot de
 * passe est bien celui qu'on croit.
 *
 * `obligatoire` bloque l'accès à l'outil tant que le mot de passe provisoire
 * issu du seed n'a pas été remplacé.
 */
export function ChangerMotDePasse({
  obligatoire = false,
  onAnnuler,
}: {
  obligatoire?: boolean;
  onAnnuler?: () => void;
}) {
  const { deconnecter, moi } = useAuth();
  const [actuel, setActuel] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [fini, setFini] = useState(false);

  async function soumettre(e: FormEvent) {
    e.preventDefault();
    setErreur(null);

    if (nouveau.length < 12) {
      setErreur('Le nouveau mot de passe doit faire au moins 12 caractères.');
      return;
    }
    if (nouveau !== confirmation) {
      setErreur('Les deux saisies ne correspondent pas.');
      return;
    }
    if (nouveau === actuel) {
      setErreur("Le nouveau mot de passe doit être différent de l'ancien.");
      return;
    }

    setEnvoi(true);
    try {
      await api.post('/api/auth/mot-de-passe', {
        motDePasseActuel: actuel,
        nouveauMotDePasse: nouveau,
      });
      setFini(true);
      // Les sessions sont révoquées côté serveur : on repart de la connexion.
      setTimeout(() => { void deconnecter(); }, 1800);
    } catch (err) {
      setErreur(
        err instanceof ErreurApi
          ? err.estReseau
            ? 'Le serveur ne répond pas. Le mot de passe n’a pas été changé.'
            : err.message
          : 'Le changement a échoué.',
      );
    } finally {
      setEnvoi(false);
    }
  }

  if (fini) {
    return (
      <div className="connexion">
        <div style={{ background: '#fff', border: '1px solid var(--line)', borderTop: '4px solid var(--vert)', padding: '26px 28px', width: 380, maxWidth: '100%' }}>
          <h1 style={{ fontSize: 19, marginBottom: 6 }}>Mot de passe changé</h1>
          <p style={{ color: 'var(--ink-2)', fontSize: 13.5 }}>
            Toutes les sessions ont été révoquées. Reconnectez-vous avec votre nouveau mot de passe.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="connexion">
      <form onSubmit={soumettre}>
        <h1>{obligatoire ? 'Choisissez votre mot de passe' : 'Changer de mot de passe'}</h1>
        <p className="hint">
          {obligatoire
            ? `Le mot de passe provisoire de ${moi?.email ?? 'ce compte'} doit être remplacé avant d'utiliser l'outil.`
            : '12 caractères minimum. Les autres sessions seront déconnectées.'}
        </p>

        <div className="field">
          <label htmlFor="mdp-actuel">Mot de passe actuel</label>
          <input
            id="mdp-actuel" type="password" autoComplete="current-password" required autoFocus
            value={actuel} onChange={(e) => setActuel(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="mdp-nouveau">Nouveau mot de passe</label>
          <input
            id="mdp-nouveau" type="password" autoComplete="new-password" required minLength={12}
            value={nouveau} onChange={(e) => setNouveau(e.target.value)}
            aria-describedby="mdp-aide"
          />
          <p id="mdp-aide" style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 4 }}>
            12 caractères minimum.
          </p>
        </div>
        <div className="field">
          <label htmlFor="mdp-confirmation">Confirmez le nouveau mot de passe</label>
          <input
            id="mdp-confirmation" type="password" autoComplete="new-password" required
            value={confirmation} onChange={(e) => setConfirmation(e.target.value)}
          />
        </div>

        {erreur && <p className="err" role="alert">{erreur}</p>}

        <button className="btn pri" type="submit" style={{ width: '100%', marginTop: 8 }} disabled={envoi}>
          {envoi ? <><span className="spin" /> Enregistrement…</> : 'Enregistrer'}
        </button>

        {!obligatoire && onAnnuler && (
          <button type="button" className="btn" style={{ width: '100%', marginTop: 8 }} onClick={onAnnuler}>
            Annuler
          </button>
        )}
        {obligatoire && (
          <button
            type="button" className="btn" style={{ width: '100%', marginTop: 8 }}
            onClick={() => { void deconnecter(); }}
          >
            Se déconnecter
          </button>
        )}
      </form>
    </div>
  );
}
